import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { ApiError, type ApiClient } from "../api/client";
import type { AppSession, DeviceStatus, PairingResult, StaffMember } from "../api/types";
import { PairingCodeInput } from "../features/pairing/PairingCodeInput";
import { t } from "../i18n";
import { LogoAnimation } from "../ui/logo/LogoAnimation";
import { useToast } from "../ui/toasts";
import { AppIcon, type AppIconName } from "./AppIcon";
import { inNativeApp } from "./native";

type AppRole = "waiter" | "courier";

const text = t.tafoApp;

/**
 * The red bar of the app (prototype ph-head): who on the left, the Tafo logo in the middle, an action on
 * the right. The logo is written when the app opens and then stands as the mark (ADR 0016); the head of
 * the next screen does not write it again.
 */
export function AppHead({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  return (
    <header className="ph-head">
      <span className="ph-who">{left}</span>
      <LogoAnimation white clock="page" />
      <span className="ph-right">{right}</span>
    </header>
  );
}

/**
 * A part of the screen that scrolls and starts at the top again when it shows something else (the
 * prototype drew every view anew): the options of a pizza tapped low in the list open at "‹ terug".
 */
function useStartAtTop<T extends HTMLElement>(scrollKey: string | undefined) {
  const element = useRef<T>(null);
  useLayoutEffect(() => {
    if (element.current !== null) element.current.scrollTop = 0;
  }, [scrollKey]);
  return element;
}

/** A column of the iPad (prototype ipad-l, ipad-r) that starts at the top for every new view. */
export function AppColumn({
  className,
  scrollKey,
  children,
}: {
  className: string;
  scrollKey: string;
  children: ReactNode;
}) {
  const column = useStartAtTop<HTMLDivElement>(scrollKey);
  return (
    <div className={className} ref={column}>
      {children}
    </div>
  );
}

/** An iPad or a tablet: the app fills the wide screen (prototype phone ipad); a phone or a Sunmi: a column. */
export const isWideDevice = (type: string): boolean => type === "ipad" || type === "tablet";

/**
 * Wide enough for the two columns of the iPad (bill left, products right): the width of the screen decides,
 * not the kind of device (Furkan, 08/10/2026: every iPhone, also the iPhone Duo). The iPhone Duo open is
 * 951 × 669 points in the width and 669 × 951 upright (Apple, 09/2026): two columns both ways; folded shut
 * (466 × 678) a column, and it changes along while it is folded open or shut. An iPhone on its side is wide
 * but low (at most 440 high) and keeps the column, as does an iPad with another app next to it.
 */
export const WIDE_MIN_PX = 640;
const WIDE_MIN_HEIGHT_PX = 500;
const wideQuery = () =>
  window.matchMedia(`(min-width: ${String(WIDE_MIN_PX)}px) and (min-height: ${String(WIDE_MIN_HEIGHT_PX)}px)`);

export function useWideScreen(): boolean {
  return useSyncExternalStore(
    (changed) => {
      const query = wideQuery();
      query.addEventListener("change", changed);
      return () => {
        query.removeEventListener("change", changed);
      };
    },
    () => wideQuery().matches,
    () => false,
  );
}

/** Whether this device has a wide screen, once the app knows which device it is. */
export const WideScreen = createContext(false);

/** A whole screen of the app: the bar, optionally tabs, and the body. */
export function AppPage({
  head,
  tabs,
  nav,
  bodyClass = "",
  wide,
  scrollKey,
  children,
}: {
  head: ReactNode;
  tabs?: ReactNode;
  /** The tabs in a bar at the bottom, with their symbol (the waiter's app). */
  nav?: ReactNode;
  bodyClass?: string;
  /** iPad or tablet: a wider screen (prototype phone ipad); by default what the device is. */
  wide?: boolean;
  /** What the body shows; when it changes, the body starts at the top again. */
  scrollKey?: string;
  children: ReactNode;
}) {
  const body = useStartAtTop<HTMLElement>(scrollKey);
  const wideScreen = useContext(WideScreen);
  return (
    <div className={`tafoapp${(wide ?? wideScreen) ? " wide" : ""}`}>
      {head}
      {tabs}
      <main className={`ph-body ${bodyClass}`.trim()} ref={body}>
        {children}
      </main>
      {nav}
    </div>
  );
}

/** Before the app can work: starting, no connection, paused shop, the owner's acceptance, a till. */
export function AppWaiting({
  kind,
  message,
  onRetry,
}: {
  kind: "starting" | "unreachable" | "blocked" | "terms" | "till";
  message?: string;
  onRetry?: () => void;
}) {
  const icon: AppIconName | null = {
    starting: null,
    unreachable: "offline" as const,
    blocked: "blocked" as const,
    terms: "document" as const,
    till: "computer" as const,
  }[kind];
  const title = {
    starting: "",
    unreachable: message ?? t.errors.network,
    blocked: text.blocked,
    terms: text.waitTerms,
    till: text.isTill,
  }[kind];
  const detail = {
    starting: "",
    unreachable: "",
    blocked: text.blockedText,
    terms: text.waitTermsText,
    till: text.isTillText(window.location.origin),
  }[kind];
  return (
    <AppPage head={<AppHead />} bodyClass="ph-login">
      {kind !== "starting" && (
        <>
          {icon !== null && (
            <p className="ph-icon" aria-hidden="true">
              <AppIcon name={icon} size={44} />
            </p>
          )}
          <p>
            <b>{title}</b>
          </p>
          {detail !== "" && <p className="proto">{detail}</p>}
          {onRetry && (
            <button type="button" className="btn ph-big" onClick={onRetry}>
              {text.retry}
            </button>
          )}
        </>
      )}
    </AppPage>
  );
}

/** The kind of device for the demo's own pairing: /app?toestel=sunmi, ipad, android (default iphone). */
const DEMO_TYPES = ["sunmi", "iphone", "ipad", "android", "tablet"];

/** First start: the owner's one-time code for the Tafo app turns this phone into a paired device. */
export function AppPairing({ api, onPaired }: { api: ApiClient; onPaired: (deviceToken: string) => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pair = async (entered: string) => {
    setBusy(true);
    setError(null);
    try {
      const paired = await api.request<PairingResult>("POST", "/device/pairing", {
        auth: "none",
        body: { code: entered, purpose: "app" },
      });
      onPaired(paired.deviceToken);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  // only in the demo (npm run demo), as the till: the app pairs itself (Furkan, 01/10/2026)
  const pairDemo = useEffectEvent((demoCode: string) => {
    setCode(demoCode);
    void pair(demoCode);
  });
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const asked = new URLSearchParams(window.location.search).get("toestel") ?? "iphone";
    const type = DEMO_TYPES.includes(asked) ? asked : "iphone";
    const controller = new AbortController();
    fetch(`/__demo/pairing-code?app=${type}`, { signal: controller.signal })
      .then(async (response) => (response.ok ? ((await response.json()) as { code?: unknown }) : null))
      .then((answer) => {
        if (typeof answer?.code === "string") pairDemo(answer.code);
      })
      .catch(() => {
        // not the demo: pair with a code from the owner
      });
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <AppPage head={<AppHead />} bodyClass="ph-login">
      <form
        className="ph-pair"
        onSubmit={(event) => {
          event.preventDefault();
          void pair(code);
        }}
      >
        <p>
          <b>{text.pairing.title}</b>
        </p>
        <p className="proto">{text.pairing.intro}</p>
        <label className="field">
          {t.pairing.code}
          <PairingCodeInput value={code} onChange={setCode} />
        </label>
        {error !== null && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <button
          type="submit"
          className="btn primary ph-big"
          disabled={busy || code.replace(/[^a-z0-9]/gi, "").length < 8}
        >
          {busy ? t.pairing.busy : t.pairing.submit}
        </button>
      </form>
    </AppPage>
  );
}

const PIN_LENGTH = 4;
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "OK"] as const;
type Key = (typeof KEYS)[number];

/** A person who may use the Tafo app, as the sign-in lists them: a name to tap, nothing else. */
interface AppName {
  id: string;
  name: string;
}

/**
 * Aanmelden (Furkan, 10/10/2026: "aanmeldgegevens: naam, code"; "naam koerier en dan persoonlijke code"):
 * first the name, tapped in the list of those who may use the app, then that person's code. The code says
 * whether this is the waiter app or the courier app. A server from before 10/10/2026 has no list: then
 * the code alone, as before.
 */
export function AppSignIn({
  api,
  status,
  onSignedIn,
}: {
  api: ApiClient;
  status: DeviceStatus;
  onSignedIn: (session: AppSession, role: AppRole) => void;
}) {
  const toast = useToast();
  /** null while loading; "code only" for an older server. */
  const [names, setNames] = useState<AppName[] | "code only" | null>(null);
  const [who, setWho] = useState<AppName | null>(null);
  const [pin, setPin] = useState("");
  const [message, setMessage] = useState("");
  const checking = useRef(false);

  useEffect(() => {
    let current = true;
    api
      .request<{ staff: AppName[] }>("GET", "/device/app-staff", { retry: true })
      .then((answer) => {
        if (current) setNames(answer.staff);
      })
      .catch(() => {
        if (current) setNames("code only");
      });
    return () => {
      current = false;
    };
  }, [api]);

  const askCode = names === "code only" || who !== null;

  const check = async (entered: string) => {
    if (checking.current) return;
    checking.current = true;
    try {
      const { role, ...session } = await api.request<{ token: string; staff: StaffMember; role?: AppRole }>(
        "POST",
        "/device/sessions",
        { body: who === null ? { pin: entered } : { pin: entered, staffId: who.id } },
      );
      // a server from before 10/10/2026 says nothing: the device stays what it was
      const now: AppRole = role ?? (status.device.role === "courier" ? "courier" : "waiter");
      toast(text.signIn.signedIn(session.staff.name, text.appNames[now]));
      onSignedIn(session, now);
    } catch (error) {
      setPin("");
      setMessage(error instanceof ApiError ? error.message : t.errors.unexpected);
      checking.current = false;
    }
  };

  const press = (key: Key) => {
    if (checking.current || !askCode) return;
    setMessage("");
    if (key === "⌫") {
      setPin((current) => current.slice(0, -1));
      return;
    }
    if (key === "OK") {
      if (pin.length === PIN_LENGTH) void check(pin);
      return;
    }
    if (pin.length >= PIN_LENGTH) return;
    const next = pin + key;
    setPin(next);
    if (next.length === PIN_LENGTH) void check(next);
  };

  // a phone has no keyboard; on a computer (the demo) the digits work too
  const onKey = useEffectEvent((event: KeyboardEvent) => {
    const key: Key | null = /^\d$/.test(event.key)
      ? (event.key as Key)
      : event.key === "Backspace"
        ? "⌫"
        : event.key === "Enter"
          ? "OK"
          : null;
    if (key === null || !askCode) return;
    event.preventDefault();
    press(key);
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      onKey(event);
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  if (names === null) return <AppPage head={<AppHead />}>{null}</AppPage>;

  if (!askCode) {
    return (
      <AppPage head={<AppHead />} bodyClass="ph-login">
        <p>
          <b>{status.shop.name}</b>
          <br />
          <span className="proto">{text.signIn.pickName}</span>
        </p>
        {names.length === 0 ? (
          <p className="proto">{text.signIn.nobody}</p>
        ) : (
          <ul className="ph-names" aria-label={text.signIn.whoAreYou}>
            {names.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  onClick={() => {
                    setWho(person);
                    setPin("");
                    setMessage("");
                  }}
                >
                  <span className="ph-initial" aria-hidden="true">
                    {person.name.trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="ph-name">{person.name}</span>
                  <AppIcon name="chevron" size={18} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {!inNativeApp() && <p className="proto">{text.signIn.homeScreen}</p>}
      </AppPage>
    );
  }

  return (
    <AppPage head={<AppHead />} bodyClass="ph-login">
      {who !== null && names !== "code only" && (
        <button
          type="button"
          className="linkbtn ph-other"
          onClick={() => {
            setWho(null);
            setPin("");
            setMessage("");
          }}
        >
          {text.signIn.otherName}
        </button>
      )}
      <p>
        <b>{who === null ? status.shop.name : who.name}</b>
        <br />
        <span className="proto">{text.signIn.prompt}</span>
      </p>
      <div className="pindots" aria-live="polite" aria-label={t.codes.entered(pin.length, PIN_LENGTH)}>
        {Array.from({ length: PIN_LENGTH }, (_, index) => (
          <span key={index} className={index < pin.length ? "on" : ""} />
        ))}
      </div>
      <p className="pinmsg" role="alert">
        {message}
      </p>
      <div className="pinpad">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={key === "OK" ? "ok" : undefined}
            aria-label={key === "⌫" ? t.codes.backspace : undefined}
            onClick={() => {
              press(key);
            }}
          >
            {key === "⌫" ? <AppIcon name="backspace" size={28} /> : key}
          </button>
        ))}
      </div>
    </AppPage>
  );
}

/**
 * After "Afmelden" (Furkan, 10/10/2026: "na afmelden beter afgemeld melding en terug knop om aan te melden
 * dan pas code"): it says so, and Aanmelden opens the sign-in for the next person.
 */
export function AppSignedOut({ name, onSignIn }: { name: string; onSignIn: () => void }) {
  return (
    <AppPage head={<AppHead />} bodyClass="ph-login">
      <p className="ph-icon" aria-hidden="true">
        <AppIcon name="check" size={44} />
      </p>
      <p>
        <b>{text.signedOut.title}</b>
        <br />
        <span className="proto">{text.signedOut.bye(name)}</span>
      </p>
      <button type="button" className="btn primary ph-big" onClick={onSignIn}>
        {text.signedOut.again}
      </button>
    </AppPage>
  );
}

/** A menu that opens under its button in the red bar; a tap elsewhere closes it. */
function usePop() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => {
      document.removeEventListener("pointerdown", away);
    };
  }, [open]);
  return { open, setOpen, box };
}

/**
 * Who is signed in, at the right of the red bar: the first letter in a circle and the name; a tap shows "Afmelden"
 * under it (Furkan, 18/10/2026; 19/10/2026: with "Koerier ⌄" at the left there is room for the name again).
 */
export function WhoButton({ name, onSignOut }: { name: string; onSignOut: () => void }) {
  const { open, setOpen, box } = usePop();
  return (
    <span className="ph-whomenu" ref={box}>
      <button
        type="button"
        className="ph-namebtn"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <span className="ph-initial" aria-hidden="true">
          {name.trim().slice(0, 1).toUpperCase()}
        </span>
        <span className="ph-nametext">{name}</span>
      </button>
      {open && (
        <span className="ph-pop">
          <button type="button" className="ph-out" onClick={onSignOut}>
            {text.signOut}
          </button>
        </span>
      )}
    </span>
  );
}

/** Where to go from the menu "Koerier ⌄": the other app, or the full till (the owner). */
export type ModeChoice = AppRole | "till";

/**
 * "Ober ⇄ Koerier" (and "Kassa" for the owner) as a menu in the red bar, next to the letter of who is signed in
 * (Furkan, 19/10/2026: instead of the switch under the bar). It shows where one works now.
 */
export function ModeMenu({
  current,
  choices,
  disabled,
  onPick,
}: {
  current: ModeChoice;
  choices: readonly ModeChoice[];
  disabled: boolean;
  onPick: (choice: ModeChoice) => void;
}) {
  const { open, setOpen, box } = usePop();
  const icon = (choice: ModeChoice): AppIconName => (choice === "till" ? "computer" : choice);
  const name = (choice: ModeChoice) => (choice === "till" ? text.mode.till : text.roles[choice]);
  return (
    <span className="ph-whomenu" ref={box}>
      <button
        type="button"
        className="ph-modebtn"
        aria-label={`${text.mode.label}: ${name(current)}`}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <AppIcon name={icon(current)} />
        <span className="ph-modename">{name(current)}</span>
        <AppIcon name="chevron" />
      </button>
      {open && (
        <span className="ph-pop ph-modepop" role="menu" aria-label={text.mode.label}>
          {choices.map((choice) => (
            <button
              key={choice}
              type="button"
              role="menuitemradio"
              aria-checked={choice === current}
              onClick={() => {
                setOpen(false);
                onPick(choice);
              }}
            >
              <AppIcon name={icon(choice)} />
              {name(choice)}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
